import type { ActionExecution } from '../commandExecutor.js';
import type { AppState } from '../../state/appState.js';
import type { SetRuntimeGraphCmd, SetDataOwnerCmd, SetDataSourcesCmd, SetShapesCmd, SetRuntimeModelCmd, SetRuntimeStateCmd, SetSceneEventsCmd, SetSceneDataContextCmd } from '../cmd/data.js';
import { findSceneTargetByNodeIdFor } from './shared.js';

export function execSetRuntimeGraphFor(appState: AppState, cmd: SetRuntimeGraphCmd): ActionExecution {
  const library = cmd.kind === 'data' ? appState.dataGraphs : cmd.kind === 'event' ? appState.eventGraphs : appState.queryGraphs;
  const previous = library[cmd.graphId];
  if (!previous && !cmd.graph) return { result: undefined, changed: false, undoActions: [] };
  if (cmd.graph) library[cmd.graphId] = structuredClone(cmd.graph);
  else delete library[cmd.graphId];
  return {
    result: undefined,
    changed: true,
    undoActions: [{ type: 'runtime:setGraph', kind: cmd.kind, graphId: cmd.graphId, graph: previous ? structuredClone(previous) : null }],
  };
}

export function execSetDataSourcesFor(appState: AppState, cmd: SetDataSourcesCmd): ActionExecution {
  const previous = structuredClone(appState.dataSources);
  appState.dataSources = structuredClone(cmd.sources);
  return {
    result: undefined,
    changed: JSON.stringify(previous) !== JSON.stringify(appState.dataSources),
    undoActions: [{ type: 'data:setSources', sources: previous }],
  };
}

export function execSetShapesFor(appState: AppState, cmd: SetShapesCmd): ActionExecution {
  const previous = structuredClone(appState.shapes);
  appState.shapes = structuredClone(cmd.shapes);
  return {
    result: undefined,
    changed: JSON.stringify(previous) !== JSON.stringify(appState.shapes),
    undoActions: [{ type: 'assets:setShapes', shapes: previous }],
  };
}

export function execSetDataOwnerFor(appState: AppState, cmd: SetDataOwnerCmd): ActionExecution {
  if (cmd.owner.type === 'document') {
    const previous = appState.data;
    appState.data = cmd.instance ? structuredClone(cmd.instance) : null;
    return {
      result: undefined,
      changed: JSON.stringify(previous) !== JSON.stringify(appState.data),
      undoActions: [{ type: 'data:setOwner', owner: cmd.owner, instance: previous ? structuredClone(previous) : null }],
    };
  }
  const target = findSceneTargetByNodeIdFor(appState, cmd.owner.nodeId);
  const node = target?.scene.nodes[cmd.owner.nodeId];
  if (!node) return { result: undefined, changed: false, undoActions: [] };
  const previous = node.behavior;
  node.behavior = cmd.instance ? structuredClone(cmd.instance) : undefined;
  return {
    result: undefined,
    changed: JSON.stringify(previous) !== JSON.stringify(node.behavior),
    undoActions: [{ type: 'data:setOwner', owner: cmd.owner, instance: previous ? structuredClone(previous) : null }],
  };
}

export function execSetRuntimeStateFor(appState: AppState, cmd: SetRuntimeStateCmd): ActionExecution {
  const previous = structuredClone(appState.runtimeState);
  appState.runtimeState = structuredClone(cmd.schema);
  return { result: undefined, changed: JSON.stringify(previous) !== JSON.stringify(appState.runtimeState), undoActions: [{ type: 'runtime:setState', schema: previous }] };
}

export function execSetRuntimeModelFor(appState: AppState, cmd: SetRuntimeModelCmd): ActionExecution {
  const previous = {
    schema: structuredClone(appState.runtimeState),
    dataGraphs: structuredClone(appState.dataGraphs),
    eventGraphs: structuredClone(appState.eventGraphs),
  };
  appState.runtimeState = structuredClone(cmd.schema);
  appState.dataGraphs = structuredClone(cmd.dataGraphs);
  appState.eventGraphs = structuredClone(cmd.eventGraphs);
  const activeGraph = appState.editorContext.mode === 'data-library'
    ? appState.dataGraphs[appState.editorContext.libraryItemId]
    : appState.editorContext.mode === 'event-library'
      ? appState.eventGraphs[appState.editorContext.libraryItemId]
      : null;
  if (activeGraph) {
    // Runtime schema edits may remove a whole dependent branch.  The graph canvas is
    // an editor session cache, so it must be refreshed from the canonical library
    // immediately instead of showing stale nodes until the next navigation.
    appState.nodes = structuredClone(activeGraph.nodes).map(node => {
      if (appState.editorContext.mode !== 'event-library') return node;
      if (node.type !== 'Set' && node.type !== 'State') return node;
      const stateName = String(node.data.state ?? '');
      const declaration = appState.runtimeState[stateName];
      return declaration ? { ...node, data: { ...node.data, valueType: declaration.type } } : node;
    });
    appState.edges = structuredClone(activeGraph.edges);
    appState.editorGraphDirty = false;
    appState.selectedNode = null;
  }
  const changed = JSON.stringify(previous) !== JSON.stringify({ schema: cmd.schema, dataGraphs: cmd.dataGraphs, eventGraphs: cmd.eventGraphs });
  return { result: undefined, changed, undoActions: [{ type: 'runtime:setModel', ...previous }] };
}

export function execSetSceneEventsFor(appState: AppState, cmd: SetSceneEventsCmd): ActionExecution {
  const target = findSceneTargetByNodeIdFor(appState, cmd.nodeId);
  const node = target?.scene.nodes[cmd.nodeId];
  if (!node) return { result: undefined, changed: false, undoActions: [] };
  const previous = node.events;
  node.events = cmd.events ? structuredClone(cmd.events) : undefined;
  return { result: undefined, changed: JSON.stringify(previous) !== JSON.stringify(node.events), undoActions: [{ type: 'event:setSceneEvents', nodeId: cmd.nodeId, events: previous ? structuredClone(previous) : null }] };
}

export function execSetSceneDataContextFor(appState: AppState, cmd: SetSceneDataContextCmd): ActionExecution {
  const target = findSceneTargetByNodeIdFor(appState, cmd.nodeId);
  const node = target?.scene.nodes[cmd.nodeId];
  if (!node) return { result: undefined, changed: false, undoActions: [] };
  const previous = node.dataContext;
  node.dataContext = cmd.context ? structuredClone(cmd.context) : undefined;
  return {
    result: undefined,
    changed: JSON.stringify(previous) !== JSON.stringify(node.dataContext),
    undoActions: [{ type: 'data:setSceneContext', nodeId: cmd.nodeId, context: previous ? structuredClone(previous) : null }],
  };
}
